import { useEffect, useState } from 'react';

export interface GeolocationState {
  position: { latitude: number; longitude: number; heading: number | null } | null;
  error: string | null;
}

function readUrlOverride(): { latitude: number; longitude: number } | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const latStr = params.get('lat');
  const lngStr = params.get('lng');
  if (latStr === null || lngStr === null) return null;
  const lat = Number(latStr);
  const lng = Number(lngStr);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { latitude: lat, longitude: lng };
}

export function useGeolocation(): GeolocationState {
  const [state, setState] = useState<GeolocationState>({ position: null, error: null });

  useEffect(() => {
    // Dev/manual override: ?lat=42.30798&lng=-71.57350 in the URL.
    // Useful for testing without depending on real GPS.
    const override = readUrlOverride();
    if (override) {
      console.debug('[cabin] geolocation: using URL override', override);
      setState({ position: { ...override, heading: null }, error: null });
      return;
    }

    if (!('geolocation' in navigator)) {
      console.warn('[cabin] geolocation not supported by this browser');
      setState({ position: null, error: 'Geolocation not supported' });
      return;
    }

    const handlePosition = (pos: GeolocationPosition, source: string) => {
      console.debug(
        `[cabin] geolocation (${source}):`,
        pos.coords.latitude.toFixed(5),
        pos.coords.longitude.toFixed(5),
        `±${Math.round(pos.coords.accuracy)}m`,
      );
      setState({
        position: {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          heading: pos.coords.heading,
        },
        error: null,
      });
    };

    // Kick off an immediate one-shot read — on Firefox/macOS this is much
    // more reliable than waiting for the first watchPosition callback.
    console.debug('[cabin] geolocation: getCurrentPosition + watchPosition');
    navigator.geolocation.getCurrentPosition(
      (pos) => handlePosition(pos, 'first-fix'),
      (err) => {
        console.warn('[cabin] geolocation getCurrentPosition error', err.code, err.message);
      },
      { enableHighAccuracy: true, maximumAge: 60_000, timeout: 15_000 },
    );

    const id = navigator.geolocation.watchPosition(
      (pos) => handlePosition(pos, 'watch'),
      (err) => {
        console.warn('[cabin] geolocation watchPosition error', err.code, err.message);
        setState((prev) => ({ ...prev, error: err.message }));
      },
      { enableHighAccuracy: true, maximumAge: 5_000, timeout: 30_000 },
    );

    return () => navigator.geolocation.clearWatch(id);
  }, []);

  return state;
}
